import type { Home } from '@lmc/ts-common'
import Actions from 'actions'
import { NAMESPACE } from 'locale'
import { homeSwitchConstants } from 'locale/homeSwitch'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

const SWITCH_HOME_MODAL_ID = 'switchHomeModal'
const REQUESTED_HOME_PARAM = 'home'

const buildRedirectUrl = () => {
  const searchParams = new URLSearchParams(window.location.search)
  searchParams.delete(REQUESTED_HOME_PARAM)
  const search = searchParams.toString()

  return `${window.location.pathname}${search ? `?${search}` : ''}`
}

type RequestedHomeSwitchState = {
  global: { home: Home }
  data: { homes: Home[] }
}

/**
 * Reads the `?home=<homeId>` deep-link param and, when it points at a
 * different home the user has access to, opens the switch-home dialog
 * pre-filled with that home. Confirming redirects back to the current URL
 * minus the param.
 */
export const useRequestedHomeSwitch = ({
  cancelRedirectUrl
}: {
  cancelRedirectUrl?: string
} = {}) => {
  const { t } = useTranslation(NAMESPACE.HOME_SWITCH)
  const dispatch = useDispatch()
  const currentHome = useSelector(
    (state: RequestedHomeSwitchState) => state.global.home
  )
  const homes = useSelector(
    (state: RequestedHomeSwitchState) => state.data.homes
  )

  const promptedForHomeRef = useRef<string | null>(null)

  const requestedHomeId = new URLSearchParams(window.location.search).get(
    REQUESTED_HOME_PARAM
  )
  const hasAccessToRequestedHome =
    !requestedHomeId ||
    requestedHomeId === currentHome?.id ||
    homes?.some((home) => home.id === requestedHomeId)
  const shouldSwitchHome = Boolean(
    requestedHomeId &&
      requestedHomeId !== currentHome?.id &&
      hasAccessToRequestedHome
  )
  const redirectTargetRef = useRef<{ homeId: string; url: string } | null>(null)
  if (
    requestedHomeId &&
    redirectTargetRef.current?.homeId !== requestedHomeId
  ) {
    redirectTargetRef.current = {
      homeId: requestedHomeId,
      url: buildRedirectUrl()
    }
  }

  useEffect(() => {
    if (
      !shouldSwitchHome ||
      !requestedHomeId ||
      promptedForHomeRef.current === requestedHomeId
    ) {
      return
    }
    promptedForHomeRef.current = requestedHomeId

    dispatch(
      Actions.openModal({
        modalId: SWITCH_HOME_MODAL_ID,
        data: {
          redirectUrl: redirectTargetRef.current?.url || buildRedirectUrl(),
          cancelRedirectUrl,
          onlyInitialEnabled: true,
          localisation: {
            title: t(homeSwitchConstants.SWITCH_HOME_TITLE),
            description: t(homeSwitchConstants.SWITCH_HOME_DESCRIPTION, {
              currentHomeName: currentHome?.name || '',
              requestedHomeName:
                homes?.find((home) => home.id === requestedHomeId)?.name || ''
            })
          }
        }
      })
    )
    dispatch(
      Actions.addForm({
        formId: SWITCH_HOME_MODAL_ID,
        initial: { home: requestedHomeId }
      })
    )
  }, [
    dispatch,
    t,
    shouldSwitchHome,
    requestedHomeId,
    currentHome?.name,
    homes,
    cancelRedirectUrl
  ])

  return { shouldSwitchHome, hasAccessToRequestedHome }
}

export default useRequestedHomeSwitch
